/**
 * Mock blueprint persistence + "save as Conductor GI" flow. The
 * saved Conductor is a Vault record with `domain: {Custom:
 * 'conductor'}` and the blueprint JSON embedded in its contract's
 * conductor-dag-adjacent spot (we store it on a side-channel since
 * our mock BehavioralContract type doesn't carry `conductor_dag`
 * on the UI side — gi-contract extended the real shape for M7).
 */

import type {
  BehavioralContract,
  Blueprint,
  DomainType,
  GiStatus,
  Hash256,
  Lineage,
  Manifest,
  PortDefinition,
  SignedEntity,
  TrustedTimestamp,
} from '../types';

import { appendActivity, upsertRecord, type GiRecord } from './store';
import {
  canonicalHash,
  canonicalJsonStringify,
  generateKeyPair,
  randomHex,
  signDigest,
} from './sign';

const STORAGE_KEY = 'gi-engine.blueprints.v1';
const MOCK_LATENCY_MS = 150;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

interface PersistedBlueprints {
  version: 1;
  drafts: Blueprint[];
}

function load(): PersistedBlueprints {
  if (typeof localStorage === 'undefined') return { version: 1, drafts: [] };
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { version: 1, drafts: [] };
  try {
    const parsed = JSON.parse(raw) as PersistedBlueprints;
    if (parsed.version !== 1) return { version: 1, drafts: [] };
    return parsed;
  } catch {
    return { version: 1, drafts: [] };
  }
}

function save(state: PersistedBlueprints): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export async function listBlueprints(): Promise<Blueprint[]> {
  await sleep(MOCK_LATENCY_MS);
  return load().drafts;
}

export async function saveBlueprintDraft(blueprint: Blueprint): Promise<void> {
  await sleep(MOCK_LATENCY_MS);
  const state = load();
  const idx = state.drafts.findIndex((b) => b.name === blueprint.name);
  if (idx >= 0) state.drafts[idx] = blueprint;
  else state.drafts.unshift(blueprint);
  save(state);
}

/** Sign the Blueprint as a brand-new Conductor GI and place it in
 *  the Vault. Returns the new Conductor's `root_id`. */
export async function publishBlueprintAsConductor(
  blueprint: Blueprint,
): Promise<string> {
  await sleep(MOCK_LATENCY_MS);

  const kp = generateKeyPair();
  const root_id = crypto.randomUUID();
  const created_at: TrustedTimestamp = {
    iso: new Date().toISOString(),
    source: 'Monotonic',
  };
  const domain: DomainType = { Custom: 'conductor' };

  // Contract: Conductor inputs = union of entry nodes' inputs;
  // Conductor outputs = terminal nodes' outputs. Parameters live on
  // child GIs, not the Conductor itself in M7 scope.
  const entries = new Set(blueprint.nodes.map((n) => n.id));
  const terminals = new Set(blueprint.nodes.map((n) => n.id));
  for (const e of blueprint.edges) {
    entries.delete(e.target_node);
    terminals.delete(e.source_node);
  }
  const conductorInputs: PortDefinition[] = [];
  const conductorOutputs: PortDefinition[] = [];
  for (const n of blueprint.nodes) {
    if (entries.has(n.id)) conductorInputs.push(...n.inputs);
    if (terminals.has(n.id)) conductorOutputs.push(...n.outputs);
  }

  const contract: BehavioralContract = {
    inputs: conductorInputs,
    outputs: conductorOutputs,
    parameters: [],
    behavior_rules: [],
    domain_adapter: domain,
    macros: [],
  };
  const contract_hash = canonicalHash(contract);

  const manifest: Manifest = {
    gi_version: '0.1.0',
    root_id,
    version: '1.0.0',
    name: blueprint.name,
    description: blueprint.description,
    domain,
    author: { name: 'you', public_key: kp.public_key },
    contract_hash,
    source_hash: canonicalHash(blueprint), // commits to the DAG
    wasm_hash: `sha256:${randomHex(32)}`,  // conductor has no WASM
    compiler: { name: 'conductor-builder', version: '0.1.0', target: 'none' },
    lineage: { root_id, depth: 0 },
    created_at,
  };
  const manifest_hash = canonicalHash(manifest);
  const signature = signDigest(
    hexToBytes(manifest_hash.slice('sha256:'.length)),
    kp,
  );
  const signed_entity: SignedEntity = {
    root_id,
    version: manifest.version,
    manifest_hash,
    signature,
    signer_public_key: kp.public_key,
    created_at,
  };

  const lineage: Lineage = {
    root_id,
    entries: [
      {
        index: 0,
        root_id,
        version: manifest.version,
        action: 'Genesis',
        parent_hash: null,
        entry_hash: canonicalHash({
          root_id,
          version: manifest.version,
          parent: null,
          blueprint_digest: manifest.source_hash,
        }),
        signer: kp.public_key,
        signed_at: created_at,
      },
    ],
  };

  const record: GiRecord = {
    manifest,
    signed_entity,
    contract,
    lineage,
    attestations: [],
    statuses: ['signed'] as GiStatus[],
    counters: { signatures: 1, verifications: 0, executions: 0, forks: 0 },
    secret_key_hex: 'conductor-secret-hex',
  };
  upsertRecord(record);

  appendActivity({ kind: 'sparked', root_id, name: manifest.name, at: created_at });
  return root_id;
}

/** Just in case the caller wants the canonical serialization. */
export function serializeBlueprint(bp: Blueprint): string {
  return canonicalJsonStringify(bp);
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

// Silent reference to anchor the Hash256 import.
void (null as Hash256 | null);
